import { useEffect, useRef, useState } from "react";

// 리더스북 쪽 그림 위의 답 칸(지인에듀 pages[].fields, 2026-10-17). 아이는 쪽 위에 바로 쓰고, 채점이
// 끝나면 같은 자리에 빨간 펜 첨삭이 얹힌다(○ · 빗금 · △, 틀린 곳에 줄 긋고 고친 말, 빠진 곳에 ∧).
//
// 좌표는 쪽 그림 폭 · 높이에 대한 비율(0~1)이라 % 로 그대로 놓는다. 글자 크기만 쪽이 그려진 실제
// 크기에서 잰다. 칸이 너무 작게 그려지는 화면(폰)에서는 칸을 누르면 아래에서 큰 입력창이 올라온다.

// 쪽이 작게 그려져도(작은 창 · 폰) 쓰고 읽을 수 있는 글자 크기. 칸이 이보다 작으면 칸을 키워 그 자리에 바로 쓴다.
const MIN_WRITING_FONT_SIZE = 15;
const MIN_READING_FONT_SIZE = 12;
const LONG_FIX_LENGTH = 18;
// 보기 칸 폭이 높이의 이만큼을 넘으면 줄 전체 칸으로 본다
const LONG_OPTION_RATIO = 8;

// 파닉스북 발음기호 칸(답이 "/æ/" 처럼 빗금 안 기호). 아이는 자판으로 기호를 못 쓰니, 칸을 누르면 정답과
// 헷갈리기 쉬운 기호를 섞어 다섯 개를 띄우고 하나를 누르게 한다(2026-10-20 대표). 자판으로 써도 된다.
// 채점은 서버 readers_grading.phonetic_key_of 가 한다(ae · sh · ch · th · ng 처럼 자판으로 쓴 것도 맞힌다).
const PHONETIC_ANSWER_PATTERN = /^\s*\/[^/]+\/\s*$/;
const PHONETIC_CHOICE_COUNT = 5;
// 정답 기호 하나에 헷갈리기 쉬운 기호(가까운 것부터). 소리 덩어리 전체가 답이면 여기서 바로 찾는다.
const CONFUSABLE_SYMBOL_LIST_BY_SYMBOL = {
  "æ": ["e", "ʌ", "ɑ", "eɪ"],
  "e": ["æ", "ɪ", "eɪ", "ʌ"],
  "ɪ": ["iː", "e", "aɪ", "æ"],
  "ʌ": ["ɑ", "æ", "ʊ", "oʊ"],
  "ɑ": ["ʌ", "æ", "oʊ", "ɔː"],
  "ɑː": ["ʌ", "æ", "oʊ", "ɔː"],
  "ʊ": ["uː", "ʌ", "oʊ", "ɪ"],
  "ə": ["ʌ", "e", "ɪ", "æ"],
  "iː": ["ɪ", "eɪ", "aɪ", "e"],
  "uː": ["ʊ", "oʊ", "juː", "ʌ"],
  "ɔː": ["ɑː", "oʊ", "ʌ", "uː"],
  "eɪ": ["æ", "e", "aɪ", "iː"],
  "aɪ": ["ɪ", "eɪ", "iː", "æ"],
  "oʊ": ["ɑ", "ʌ", "uː", "ɔː"],
  "juː": ["uː", "ʊ", "iː", "oʊ"],
  "ʃ": ["s", "tʃ", "ʒ", "θ"],
  "tʃ": ["ʃ", "dʒ", "t", "k"],
  "dʒ": ["tʃ", "ʒ", "d", "ɡ"],
  "θ": ["ð", "s", "f", "t"],
  "ð": ["θ", "d", "z", "v"],
  "ŋ": ["n", "m", "ŋk", "ɡ"],
  "ʒ": ["ʃ", "dʒ", "z", "s"],
  "ˈ": ["ˌ", "ː", "ə", "."],
  "p": ["b", "t", "k", "f"],
  "b": ["p", "d", "v", "m"],
  "t": ["d", "k", "p", "θ"],
  "d": ["t", "b", "ɡ", "ð"],
  "k": ["ɡ", "t", "p", "h"],
  "ɡ": ["k", "d", "dʒ", "ŋ"],
  "f": ["v", "θ", "p", "h"],
  "v": ["f", "b", "w", "ð"],
  "s": ["z", "ʃ", "θ", "ts"],
  "z": ["s", "ʒ", "ð", "dʒ"],
  "m": ["n", "ŋ", "b", "w"],
  "n": ["m", "ŋ", "l", "d"],
  "l": ["r", "n", "w", "j"],
  "r": ["l", "w", "j", "ɹ"],
  "w": ["r", "v", "j", "uː"],
  "h": ["f", "k", "θ", "ʃ"],
  "j": ["dʒ", "ʒ", "iː", "w"],
};
// 길이 순으로 자른다(tʃ 를 t 와 ʃ 로 쪼개지 않게)
const PHONETIC_TOKEN_LIST = Object.keys(CONFUSABLE_SYMBOL_LIST_BY_SYMBOL).sort((one, other) => other.length - one.length);
const splitPhonetic = (text) => {
  const tokenList = [];
  let rest = text;
  while (rest) {
    const token = PHONETIC_TOKEN_LIST.find((one) => rest.startsWith(one)) || rest[0];
    tokenList.push(token);
    rest = rest.slice(token.length);
  }
  return tokenList;
};
// 칸마다 늘 같은 순서로 섞는다(다시 눌러도 자리가 바뀌지 않게)
const seededShuffle = (list, seedText) => {
  let seed = Array.from(String(seedText)).reduce((sum, character) => (sum * 31 + character.charCodeAt(0)) >>> 0, 7);
  const result = list.slice();
  for (let index = result.length - 1; index > 0; index -= 1) {
    seed = (seed * 1103515245 + 12345) >>> 0;
    const other = seed % (index + 1);
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
};
// 정답 + 헷갈리는 기호 넷. 여러 소리 덩어리(/st/ · /ntʃ/)는 한 소리씩 바꿔 끼워 만든다.
const phoneticChoiceListOf = (field) => {
  const answer = String(field.answer || "").trim().replace(/^\/|\/$/g, "");
  const candidateList = [];
  const add = (one) => {
    if (one && one !== answer && !candidateList.includes(one)) candidateList.push(one);
  };
  (CONFUSABLE_SYMBOL_LIST_BY_SYMBOL[answer] || []).forEach(add);
  const tokenList = splitPhonetic(answer);
  for (let rank = 0; rank < 4 && candidateList.length < PHONETIC_CHOICE_COUNT - 1; rank += 1) {
    tokenList.forEach((token, position) => {
      const replacement = (CONFUSABLE_SYMBOL_LIST_BY_SYMBOL[token] || [])[rank];
      if (replacement) add([...tokenList.slice(0, position), replacement, ...tokenList.slice(position + 1)].join(""));
    });
  }
  return seededShuffle([answer, ...candidateList.slice(0, PHONETIC_CHOICE_COUNT - 1)], field.id);
};
const SYMBOL_PAD_WIDTH = 250;
const isPhoneticField = (field) => typeof field.answer === "string" && PHONETIC_ANSWER_PATTERN.test(field.answer);

const percentBox = (box) => ({
  left: `${box.x * 100}%`,
  top: `${box.y * 100}%`,
  width: `${box.w * 100}%`,
  height: `${box.h * 100}%`,
});

// 손으로 그린 듯한 표시. viewBox 40x40 안에서 그린다.
const MARK_PATH = {
  ok: "M20 4 C31 3 37 11 36 21 C35 31 27 37 18 36 C9 35 3 27 4 18 C5 10 12 5 22 5",
  no: "M8 34 C16 24 24 14 34 4",
  half: "M20 4 L36 34 L4 34 Z",
};

export const VerdictMark = ({ verdict, point, size }) => {
  if (!point || !MARK_PATH[verdict]) return null;
  return (
    <svg
      className="page-field__mark"
      viewBox="0 0 40 40"
      style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%`, width: size, height: size }}
    >
      <path d={MARK_PATH[verdict]} />
    </svg>
  );
};

// 채점된 글: 그대로 둔 조각, 줄 그은 조각(위에 고친 말), 빠진 자리(∧ 와 넣을 말)
export const CorrectedText = ({ segmentList, fallbackText }) => {
  if (!segmentList || !segmentList.length) return <>{fallbackText}</>;
  return (
    <>
      {segmentList.map((segment, index) => {
        if (segment.kind === "wrong" && String(segment.fix || "").length > LONG_FIX_LENGTH * 2) {
          // 문장을 통째로 고친 것은 줄 위에 얹으면 칸을 넘친다. 그은 줄 아래 따로 쓴다.
          return (
            <span key={index}>
              <span className="page-field__wrong page-field__wrong--whole">{segment.text}</span>
              <span className="page-field__fix-block">{segment.fix}</span>
            </span>
          );
        }
        if (segment.kind === "wrong") {
          return (
            <span className="page-field__wrong" key={index}>
              {segment.text}
              <span className="page-field__fix">{segment.fix}</span>
            </span>
          );
        }
        if (segment.kind === "insert") {
          // 빠진 말이 길면(뒷문장 통째로 등) 줄 위에 다 쓰면 윗줄을 덮는다. 작게 줄 바꿔 쓴다.
          const isLong = String(segment.fix || "").length > LONG_FIX_LENGTH;
          return (
            <span className="page-field__insert" key={index}>
              <span className="page-field__caret">∧</span>
              <span className={`page-field__fix${isLong ? " page-field__fix--long" : ""}`}>{segment.fix}</span>
            </span>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </>
  );
};

const PageFieldLayer = ({ fieldList, responses, results, onChange, onCommit, isEditable, noteNumberById, onOptionPick }) => {
  const layerRef = useRef(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [pickedLeft, setPickedLeft] = useState(null);
  // 기호 단추를 띄운 칸
  const [symbolFieldId, setSymbolFieldId] = useState(null);

  useEffect(() => {
    if (!layerRef.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setPageSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    );
    observer.observe(layerRef.current);
    return () => observer.disconnect();
  }, []);

  const markSize = Math.max(18, pageSize.width * 0.042);
  const fontSizeOf = (box) => Math.min(box.h * pageSize.height * 0.95, pageSize.width * 0.032);
  // 칸보다 긴 글은 칸 안에 들도록 글자를 줄인다(손글씨 글꼴 한 글자 폭을 0.55em 으로 어림)
  const fittedFontSizeOf = (fontSize, widthPixel, text) => {
    const length = String(text || "").length;
    if (!length) return fontSize;
    return Math.max(MIN_READING_FONT_SIZE - 1, Math.min(fontSize, widthPixel / (length * 0.55)));
  };

  const renderTextField = (field) => {
    const value = responses[field.id] ?? "";
    const result = results[field.id];
    const fontSize = fontSizeOf(field.box);
    const noteNumber = noteNumberById[field.id];

    if (result || !isEditable) {
      return (
        <div
          key={field.id}
          className={`page-field__hand${field.type === "textarea" ? " page-field__hand--multi" : ""}${
            ""
          }`}
          style={{
            ...percentBox(field.box),
            fontSize:
              field.type === "textarea"
                ? Math.max(fontSize, MIN_READING_FONT_SIZE)
                : fittedFontSizeOf(Math.max(fontSize, MIN_READING_FONT_SIZE), field.box.w * pageSize.width, value),
          }}
        >
          {/* 칸은 flex 라 조각을 바로 넣으면 조각 사이 띄어쓰기가 사라진다. 한 줄 글로 감싼다. */}
          <span className="page-field__line">
            {result ? <CorrectedText segmentList={result.segments} fallbackText={value} /> : value}
            {noteNumber && <span className="page-field__note">{noteNumber}</span>}
          </span>
        </div>
      );
    }

    // 칸이 작게 그려지면 그 자리에서 칸을 키워 바로 쓴다(따로 입력창을 띄우면 쪽이 가려져 무엇을 쓰는지 안 보인다).
    // 글자를 읽을 만한 크기로 올리고, 칸은 밑줄 자리에 맞춰 위로 · 오른쪽으로 넓힌다.
    const isSmall = fontSize < MIN_WRITING_FONT_SIZE && field.type !== "textarea";
    let boxStyle = percentBox(field.box);
    if (isSmall) {
      const writingFontSize = MIN_WRITING_FONT_SIZE;
      const height = writingFontSize * 1.5;
      const left = field.box.x * pageSize.width;
      const width = Math.min(
        Math.max(field.box.w * pageSize.width, field.type === "number_box" ? height : writingFontSize * 7),
        pageSize.width - left
      );
      boxStyle = {
        left: `${left}px`,
        top: `${(field.box.y + field.box.h) * pageSize.height - height + 2}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    }
    const inputFontSize = isSmall
      ? MIN_WRITING_FONT_SIZE
      : field.type === "textarea"
        ? fontSize
        : fittedFontSizeOf(fontSize, field.box.w * pageSize.width, value);
    const commonProps = {
      className: `page-field__input${field.type === "number_box" ? " page-field__input--center" : ""}${
        isSmall ? " page-field__input--lifted" : ""
      }${value ? " page-field__input--filled" : ""}${field.type === "textarea" ? " page-field__input--multi" : ""}`,
      style: {
        ...boxStyle,
        fontSize: inputFontSize,
      },
      value,
      spellCheck: false,
      autoCapitalize: "off",
      autoComplete: "off",
      onChange: (event) => onChange(field.id, event.target.value),
      // 칸을 떠나면 쓴 글이 손글씨로 쪽 위에 앉는다. 그 자리에서 바로 저장한다.
      onBlur: () => {
        setSymbolFieldId(null);
        if (onCommit) onCommit();
      },
      onFocus: () => setSymbolFieldId(isPhoneticField(field) ? field.id : null),
    };
    if (field.type === "textarea") return <textarea key={field.id} {...commonProps} />;
    const symbolPad = symbolFieldId === field.id && (
      <div
        key={`${field.id}-symbols`}
        className="page-field__symbols"
        // 칸 바로 아래, 쪽 밖으로 나가지 않게 왼쪽을 당긴다(단추 판 폭은 쪽 폭 안으로 줄어든다)
        style={{
          top: `calc(${boxStyle.top} + ${boxStyle.height} + 6px)`,
          left: `${Math.max(4, Math.min(field.box.x * pageSize.width, pageSize.width - SYMBOL_PAD_WIDTH - 4))}px`,
          maxWidth: `${pageSize.width - 8}px`,
        }}
        // 단추를 눌러도 칸에서 커서가 빠지지 않게 한다(빠지면 단추가 닫힌다)
        onMouseDown={(event) => event.preventDefault()}
      >
        {phoneticChoiceListOf(field).map((symbol) => (
          <button
            type="button"
            key={symbol}
            className={value === symbol ? "is-picked" : ""}
            onClick={(event) => {
              onChange(field.id, symbol);
              // 고르면 다 쓴 것이다. 칸을 닫으면 손글씨로 앉고 저장된다
              const input = event.currentTarget.parentElement?.previousElementSibling;
              setTimeout(() => input && input.blur && input.blur(), 0);
            }}
          >
            {symbol}
          </button>
        ))}
      </div>
    );
    return [
      <input
        key={field.id}
        {...commonProps}
        // 엔터를 누르면 다 쓴 것이다. 쓴 글이 손글씨로 쏙 들어가고 다음 칸으로 넘어간다(탭과 같다, 2026-10-19 대표).
        // 펼친 두 쪽에서 다음 칸이 없으면 칸만 닫는다.
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          // 한글을 조합하는 중이면 조합이 끝난 뒤에 옮긴다(바로 옮기면 마지막 글자가 빠질 수 있다)
          const target = event.currentTarget;
          setTimeout(() => {
            const inputList = Array.from(document.querySelectorAll(".page-field__input:not(:disabled)"));
            const next = inputList[inputList.indexOf(target) + 1];
            if (next) next.focus();
            else target.blur();
          }, 0);
        }}
        inputMode={field.type === "number_box" ? "numeric" : "text"}
        maxLength={field.type === "number_box" ? 2 : 300}
      />,
      symbolPad,
    ];
  };

  const renderChoiceField = (field) => {
    const picked = responses[field.id];
    const result = results[field.id];
    return (field.options || []).map((option, optionIndex) => {
      const isPicked = picked !== undefined && picked !== null && String(picked) === String(option.value);
      const isAnswer = result && result.verdict === "no" && String(field.answer) === String(option.value);
      // 세로로 늘어선 보기는 칸이 줄 폭 전체다. 동그라미를 줄 전체에 그리면 글을 가로지르므로 번호(①)에만 그린다.
      // 누르는 자리는 줄 전체 그대로다.
      const boxWidthPixel = option.box.w * pageSize.width;
      const boxHeightPixel = option.box.h * pageSize.height;
      const isLongLine = boxWidthPixel > boxHeightPixel * LONG_OPTION_RATIO;
      return (
        <button
          type="button"
          key={`${field.id}-${optionIndex}`}
          className={`page-field__option${isPicked ? " is-picked" : ""}${isAnswer ? " is-answer" : ""}`}
          style={percentBox(option.box)}
          disabled={!isEditable || Boolean(result)}
          onClick={() => {
            if (onOptionPick) onOptionPick(field.id, option.value);
            onChange(field.id, option.value);
          }}
          aria-label={String(option.value)}
        >
          <span
            className="page-field__option-mark"
            style={isLongLine ? { width: boxHeightPixel * 1.1, left: -boxHeightPixel * 0.25 } : undefined}
          />
        </button>
      );
    });
  };

  // 파닉스 책(2026-10-19): 맞는 보기를 모두 고르는 칸. 누를 때마다 넣고 뺀다.
  const renderMultiChoiceField = (field) => {
    const pickedList = Array.isArray(responses[field.id]) ? responses[field.id].map(String) : [];
    const result = results[field.id];
    const answerList = (Array.isArray(field.answer) ? field.answer : []).map(String);
    return (field.options || []).map((option, optionIndex) => {
      const value = String(option.value);
      const isPicked = pickedList.includes(value);
      const isAnswer = result && result.verdict !== "ok" && answerList.includes(value) && !isPicked;
      return (
        <button
          type="button"
          key={`${field.id}-${optionIndex}`}
          className={`page-field__option${isPicked ? " is-picked" : ""}${isAnswer ? " is-answer" : ""}`}
          style={percentBox(option.box)}
          disabled={!isEditable || Boolean(result)}
          onClick={() =>
            onChange(field.id, isPicked ? pickedList.filter((one) => one !== value) : [...pickedList, value])
          }
          aria-label={option.text || value}
        >
          <span className="page-field__option-mark" />
        </button>
      );
    });
  };

  // 파닉스 책: 동그라미 칠 자리(문단 속 낱말 · 낱말 찾기 · 목표 글자소). 칸이 곧 정답 자리다. 누르면 동그라미, 다시 누르면 지운다.
  const renderCircleField = (field) => {
    const isPicked = responses[field.id] === true;
    const result = results[field.id];
    const isAnswer = result && result.verdict === "no";
    return (
      <button
        type="button"
        key={field.id}
        className={`page-field__option page-field__option--circle${isPicked ? " is-picked" : ""}${isAnswer ? " is-answer" : ""}`}
        style={percentBox(field.box)}
        disabled={!isEditable || Boolean(result)}
        onClick={() => onChange(field.id, !isPicked)}
        aria-label={String(field.answer || "")}
      >
        <span className="page-field__option-mark" />
      </button>
    );
  };

  const renderMatchField = (field) => {
    const pairList = Array.isArray(responses[field.id]) ? responses[field.id] : [];
    const result = results[field.id];
    const pointOf = (list, value) => list.find((one) => String(one.value) === String(value));
    const toggleLeft = (value) => {
      if (!isEditable || result) return;
      const existing = pairList.find((pair) => String(pair[0]) === String(value));
      if (existing) {
        onChange(field.id, pairList.filter((pair) => pair !== existing));
        return;
      }
      setPickedLeft(value);
    };
    const pickRight = (value) => {
      if (!isEditable || result || pickedLeft === null) return;
      const nextList = pairList.filter((pair) => String(pair[0]) !== String(pickedLeft) && String(pair[1]) !== String(value));
      onChange(field.id, [...nextList, [pickedLeft, value]]);
      setPickedLeft(null);
    };
    return (
      <div key={field.id} className="page-field__match">
        <svg viewBox="0 0 1000 1000" preserveAspectRatio="none">
          {pairList.map(([leftValue, rightValue]) => {
            const left = pointOf(field.left || [], leftValue);
            const right = pointOf(field.right || [], rightValue);
            if (!left || !right) return null;
            // 파닉스 책은 정답 짝을 answer 에 싣는다(잇는 점 value 가 서로 다르다). 리더스북은 같은 value 끼리다.
            const answerPairList = Array.isArray(field.answer) ? field.answer : [];
            const isRight = answerPairList.length
              ? answerPairList.some((pair) => String(pair[0]) === String(leftValue) && String(pair[1]) === String(rightValue))
              : String(leftValue) === String(rightValue);
            const isWrong = result && !isRight;
            return (
              <line
                key={`${leftValue}-${rightValue}`}
                x1={left.x * 1000}
                y1={left.y * 1000}
                x2={right.x * 1000}
                y2={right.y * 1000}
                className={isWrong ? "is-wrong" : ""}
              />
            );
          })}
        </svg>
        {(field.left || []).map((point) => (
          <button
            type="button"
            key={`left-${point.value}`}
            className={`page-field__dot${String(pickedLeft) === String(point.value) ? " is-picked" : ""}`}
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
            onClick={() => toggleLeft(point.value)}
            aria-label={String(point.value)}
          />
        ))}
        {(field.right || []).map((point) => (
          <button
            type="button"
            key={`right-${point.value}`}
            className="page-field__dot"
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
            onClick={() => pickRight(point.value)}
            aria-label={String(point.value)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="page-field" ref={layerRef}>
      {pageSize.width > 0 &&
        fieldList.map((field) => {
          if (field.type === "choice") return renderChoiceField(field);
          if (field.type === "multi_choice") return renderMultiChoiceField(field);
          if (field.type === "circle") return renderCircleField(field);
          if (field.type === "match") return renderMatchField(field);
          // 서 · 논술형(문법책 essay)은 여러 줄 쓰기 칸과 같다. 매기지 않고 선생님이 본다
          if (field.type === "essay") return renderTextField({ ...field, type: "textarea" });
          return renderTextField(field);
        })}
      {pageSize.width > 0 &&
        fieldList.map((field) =>
          results[field.id] ? (
            <VerdictMark key={`mark-${field.id}`} verdict={results[field.id].verdict} point={field.number} size={markSize} />
          ) : null
        )}
    </div>
  );
};

export default PageFieldLayer;
